import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { formatDateTime } from './utils/dateFormatter';
import {
	Card,
	CardBody,
	Button,
	Chip,
	Skeleton,
	Select,
	SelectItem,
	addToast
} from '@heroui/react';
import { parseDate } from '@internationalized/date';
import { AppDatePicker } from './components/AppDatePicker';
import { Gauge, ChevronLeft, ChevronRight, X } from 'lucide-react';
import ResourcesApi from '../../api/resourcesApi.js';
import HeroUIBreadcrumb from '../breadcrumbs/HeroUIBreadcrumb.js';

// Convert YYYY-MM-DD string to CalendarDate for HeroUI DatePicker
const stringToCalendarDate = dateString => {
	if (!dateString || typeof dateString !== 'string') return null;
	try {
		if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
			return parseDate(dateString);
		}
		return null;
	} catch {
		return null;
	}
};

// Convert CalendarDate to YYYY-MM-DD string
const calendarDateToString = calendarDate => {
	if (!calendarDate) return '';
	const yyyy = calendarDate.year;
	const mm = String(calendarDate.month).padStart(2, '0');
	const dd = String(calendarDate.day).padStart(2, '0');
	return `${yyyy}-${mm}-${dd}`;
};

// Get organization unique from URL
const getOrgUnique = () => {
	const match = window.location.pathname.match(/\/org\/([^/]+)\//);
	return match ? match[1] : '';
};

// Period presets
const PERIOD_OPTIONS = [
	{ value: 'day', label: 'Day' },
	{ value: 'week', label: 'Week' },
	{ value: 'month', label: 'Month' }
];

function SimulatorMetersPage() {
	const { simulatorId } = useParams();
	const navigate = useNavigate();
	const orgUnique = getOrgUnique();

	const [simulator, setSimulator] = React.useState(null);
	const [counters, setCounters] = React.useState([]);
	const [history, setHistory] = React.useState([]);
	const [pilots, setPilots] = React.useState([]);
	const [sicPilots, setSicPilots] = React.useState([]);
	const [loading, setLoading] = React.useState(true);

	// Filter state - empty dates means no date filtering (show all)
	const [period, setPeriod] = React.useState(null); // null = All time
	const [startDate, setStartDate] = React.useState('');
	const [endDate, setEndDate] = React.useState('');
	const [selectedPilots, setSelectedPilots] = React.useState([]);
	const [selectedSicPilots, setSelectedSicPilots] = React.useState([]);
	const [timeVariable, setTimeVariable] = React.useState('start');

	// Pagination (cursor-based)
	const [position, setPosition] = React.useState(0);
	const [hasNextPage, setHasNextPage] = React.useState(false);
	const [nextPosition, setNextPosition] = React.useState(0);
	const [positionHistory, setPositionHistory] = React.useState([]);

	const resetPagination = () => {
		setPosition(0);
		setNextPosition(0);
		setHasNextPage(false);
		setPositionHistory([]);
	};

	// Set date range when period is selected
	const handlePeriodChange = newPeriod => {
		setPeriod(newPeriod);
		resetPagination();

		const now = new Date();
		let start, end;

		switch (newPeriod) {
			case 'day':
				start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
				break;
			case 'week':
				const dayOfWeek = now.getDay();
				start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - dayOfWeek);
				end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + (6 - dayOfWeek));
				break;
			case 'month':
				start = new Date(now.getFullYear(), now.getMonth(), 1);
				end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
				break;
			default:
				// 'all' - clear dates
				setStartDate('');
				setEndDate('');
				return;
		}

		setStartDate(start.toISOString().split('T')[0]);
		setEndDate(end.toISOString().split('T')[0]);
	};

	// Fetch simulator data
	const fetchSimulatorData = React.useCallback(async () => {
		if (!simulatorId) return;
		try {
			const response = await ResourcesApi.getSimulator(parseInt(simulatorId, 10));
			const data = response.data?.data || response.data || response;
			setSimulator(data);
			setCounters(data.counters || []);
		} catch (err) {
			console.error('Failed to load simulator:', err);
		}
	}, [simulatorId]);

	// Fetch counters history
	const fetchHistory = React.useCallback(async () => {
		if (!simulatorId) return;
		setLoading(true);
		try {
			const response = await ResourcesApi.getSimulatorCountersHistory(parseInt(simulatorId, 10), {
				position,
				startTime: startDate || null,
				finishTime: endDate || null,
				timeVariable,
				pilotIds: selectedPilots.map(id => parseInt(id, 10)),
				sicPilotIds: selectedSicPilots.map(id => parseInt(id, 10)),
				sortValue: timeVariable === 'modify' ? 'dateOfChange' : 'eventStartTime',
				sortType: 'DESC'
			});
			const data = response.data?.data || response.data || {};

			if (data.countersHistory !== undefined) {
				setHistory(data.countersHistory);
				setHasNextPage(data.next > 0);
				setNextPosition(data.position || 0);
				if (data.pilots && pilots.length === 0) setPilots(data.pilots);
				if (data.sicPilots && sicPilots.length === 0) setSicPilots(data.sicPilots);
			} else if (Array.isArray(data)) {
				setHistory(data);
				setHasNextPage(false);
			} else {
				setHistory([]);
				setHasNextPage(false);
			}
		} catch (err) {
			console.error('Failed to load counters history:', err);
			let errorMessage = 'Failed to load meters history';
			if (err.response?.status === 422) {
				errorMessage = 'Invalid filter values. Please check your date range.';
			} else if (err.response?.status === 404) {
				errorMessage = 'Simulator not found';
			} else if (err.message) {
				errorMessage = err.message;
			}
			addToast({
				title: 'Error',
				description: errorMessage,
				color: 'danger'
			});
			setHistory([]);
		} finally {
			setLoading(false);
		}
	}, [simulatorId, position, startDate, endDate, timeVariable, selectedPilots, selectedSicPilots]);

	// Initial data fetch
	React.useEffect(() => {
		fetchSimulatorData();
	}, [fetchSimulatorData]);

	// Fetch history on mount and when position changes
	React.useEffect(() => {
		fetchHistory();
	}, [fetchHistory]);


	// Navigate period (prev/next)
	const navigatePeriod = direction => {
		if (!period || !startDate || !endDate) return;

		const start = new Date(startDate);
		const end = new Date(endDate);
		let delta;

		switch (period) {
			case 'day':
				delta = direction === 'prev' ? -1 : 1;
				start.setDate(start.getDate() + delta);
				end.setDate(end.getDate() + delta);
				break;
			case 'week':
				delta = direction === 'prev' ? -7 : 7;
				start.setDate(start.getDate() + delta);
				end.setDate(end.getDate() + delta);
				break;
			case 'month':
				delta = direction === 'prev' ? -1 : 1;
				start.setMonth(start.getMonth() + delta);
				end.setDate(1);
				end.setMonth(start.getMonth() + 1);
				end.setDate(0);
				break;
			default:
				return;
		}

		resetPagination();
		setStartDate(start.toISOString().split('T')[0]);
		setEndDate(end.toISOString().split('T')[0]);
	};

	// Clear filters - reset to show all
	const handleClearFilters = () => {
		setSelectedPilots([]);
		setSelectedSicPilots([]);
		setTimeVariable('start');
		setPeriod(null);
		setStartDate('');
		setEndDate('');
		resetPagination();
	};

	// Back navigation
	const handleBack = () => {
		navigate(`/simulator/${simulatorId}`);
	};

	// Navigate to calendar day view when clicking on event row
	const handleEventClick = entry => {
		if (!entry.eventId || !entry.eventStartTime) return;

		const eventDate = new Date(entry.eventStartTime * 1000);
		const dayTimestamp = Math.floor(
			new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate()).getTime() / 1000
		);

		const yyyy = eventDate.getFullYear();
		const mm = String(eventDate.getMonth() + 1).padStart(2, '0');
		const dd = String(eventDate.getDate()).padStart(2, '0');
		const dateStr = `${yyyy}-${mm}-${dd}`;

		const timezone = window.serverData?.timeZoneId || window.globals?.timeZone?.id || 'UTC';
		const url = `/org/${orgUnique}/calendar/day/${dateStr}/${timezone}/#?day=${dayTimestamp}&org=${orgUnique}&action=event-view&event=${entry.eventId}&startTime=${entry.eventStartTime}&finishTime=${entry.eventFinishTime || entry.eventStartTime}`;

		window.open(url, '_blank');
	};

	// Breadcrumbs
	const breadcrumbCustomItems = [
		{ label: window.location.pathname.includes('/hangars') ? 'Aircraft Status' : 'Resources', href: `/org/${orgUnique}/${window.location.pathname.includes('/hangars') ? 'hangars' : 'resources'}/` },
		{ label: simulator?.name || 'Simulator', href: `/org/${orgUnique}/resources/simulator/${simulatorId}` }
	];

	// Loading skeleton
	if (loading && !simulator) {
		return (
			<div className="max-w-[1400px] mx-auto px-4 py-8">
				<Skeleton className="h-8 w-48 rounded-lg mb-6" />
				<Skeleton className="h-24 w-full rounded-lg mb-4" />
				<Skeleton className="h-12 w-full rounded-lg mb-4" />
				<div className="space-y-2">
					{[1, 2, 3].map(i => (
						<Skeleton key={i} className="h-16 w-full rounded-lg" />
					))}
				</div>
			</div>
		);
	}

	return (
		<div className="max-w-[1400px] mx-auto px-4 py-8">
			<HeroUIBreadcrumb currentPage="Meters History" customItems={breadcrumbCustomItems} />

			{/* Page Header */}
			<div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
				<div className="flex items-center gap-4">
					<div className="p-3 rounded-full bg-gradient-to-br from-violet-500 to-purple-600">
						<Gauge className="w-8 h-8 text-white" />
					</div>
					<div>
						<h1 className="text-2xl font-bold">{simulator?.name || 'Simulator'}</h1>
						<p className="text-default-500 text-sm">
							{simulator?.registrationNumber}
							{(simulator?.manufacturer || simulator?.model) && ' · '}
							{[simulator?.manufacturer, simulator?.model].filter(Boolean).join(' ')}
							{' · Meters History'}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-4 self-end md:self-auto">
					{/* Current Meter Values */}
					<div className="flex gap-4 flex-wrap">
						{counters.map((counter, index) => (
							<div key={counter.id || `counter-${index}`} className="text-center">
								<p className="text-xs text-default-500 uppercase">{counter.name}</p>
								<p className="text-lg font-bold">{counter.value || 0}</p>
							</div>
						))}
					</div>
					<Button variant="light" isIconOnly onPress={handleBack}>
						<X className="w-5 h-5" />
					</Button>
				</div>
			</div>

			{/* Filters */}
			<Card className="mb-6">
				<CardBody>
					<div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-end flex-wrap">
						{/* Period Navigation */}
						{/* Mobile: Select only */}
						<div className="max-md:block md:hidden w-full">
							<Select
								label="Period"
								size="sm"
								selectedKeys={[period || 'all']}
								onSelectionChange={keys => {
									const val = Array.from(keys)[0];
									handlePeriodChange(val === 'all' ? null : val);
								}}
								className="w-full"
							>
								<SelectItem key="all">All</SelectItem>
								{PERIOD_OPTIONS.map(opt => (
									<SelectItem key={opt.value}>{opt.label}</SelectItem>
								))}
							</Select>
						</div>

						{/* Tablet/Desktop: Button group with arrows */}
						<div className="max-md:hidden md:flex items-center gap-2">
							<Button isIconOnly size="sm" variant="flat" isDisabled={!period} onPress={() => navigatePeriod('prev')}>
								<ChevronLeft className="w-4 h-4" />
							</Button>
							<Button
								key={`all-${period}`}
								size="sm"
								variant={period === null ? 'solid' : 'flat'}
								color={period === null ? 'primary' : 'default'}
								onPress={() => handlePeriodChange(null)}
							>
								All
							</Button>
							{PERIOD_OPTIONS.map(opt => (
								<Button
									key={`${opt.value}-${period}`}
									size="sm"
									variant={period === opt.value ? 'solid' : 'flat'}
									color={period === opt.value ? 'primary' : 'default'}
									onPress={() => handlePeriodChange(opt.value)}
								>
									{opt.label}
								</Button>
							))}
							<Button isIconOnly size="sm" variant="flat" isDisabled={!period} onPress={() => navigatePeriod('next')}>
								<ChevronRight className="w-4 h-4" />
							</Button>
						</div>

						{/* Date Range */}
						<div className="flex gap-2 items-center w-full sm:w-auto">
							<AppDatePicker
								label="From"
								labelPlacement="inside"
								value={stringToCalendarDate(startDate)}
								onChange={val => { setStartDate(calendarDateToString(val)); resetPagination(); }}
								className="flex-1 sm:w-40 sm:flex-none"
							/>
							<AppDatePicker
								label="To"
								labelPlacement="inside"
								value={stringToCalendarDate(endDate)}
								onChange={val => { setEndDate(calendarDateToString(val)); resetPagination(); }}
								className="flex-1 sm:w-40 sm:flex-none"
							/>
						</div>

						{/* Pilot Filter */}
						{pilots.length > 0 && (
							<Select
								label="Pilot"
								placeholder="All Pilots"
								size="sm"
								selectionMode="multiple"
								selectedKeys={new Set(selectedPilots)}
								onSelectionChange={keys => { setSelectedPilots(Array.from(keys)); resetPagination(); }}
								className="w-full sm:w-48"
							>
								{pilots.map(pilot => (
									<SelectItem key={String(pilot.pilotID || pilot.id)}>
										{`${pilot.pilotName || pilot.name} ${pilot.pilotSurname || pilot.surname || ''}`.trim()}
									</SelectItem>
								))}
							</Select>
						)}

						{/* SIC Pilot Filter */}
						{sicPilots.length > 0 && (
							<Select
								label="SIC Pilot"
								placeholder="All SIC Pilots"
								size="sm"
								selectionMode="multiple"
								selectedKeys={new Set(selectedSicPilots)}
								onSelectionChange={keys => { setSelectedSicPilots(Array.from(keys)); resetPagination(); }}
								className="w-full sm:w-48"
							>
								{sicPilots.map(pilot => (
									<SelectItem key={String(pilot.id)}>
										{`${pilot.sicName} ${pilot.sicSurname || ''}`.trim()}
									</SelectItem>
								))}
							</Select>
						)}

						{/* Time Variable Filter */}
						<Select
							label="Sort By"
							size="sm"
							selectedKeys={[timeVariable]}
							onSelectionChange={keys => { setTimeVariable(Array.from(keys)[0] || 'start'); resetPagination(); }}
							className="w-full sm:w-44"
						>
							<SelectItem key="start">Event Time</SelectItem>
							<SelectItem key="modify">Last Modified</SelectItem>
						</Select>

						{/* Clear Filters */}
						<Button
							size="sm"
							variant="flat"
							startContent={<X className="w-4 h-4" />}
							onPress={handleClearFilters}
							className="w-full sm:w-auto"
						>
							Clear
						</Button>
					</div>
				</CardBody>
			</Card>

			{/* History Table */}
			<Card>
				<CardBody>
					{loading ? (
						<div className="space-y-2">
							{[1, 2, 3].map(i => (
								<Skeleton key={i} className="h-16 w-full rounded-lg" />
							))}
						</div>
					) : history.length === 0 ? (
						<div className="text-center py-12 text-default-500">
							No meter history found for the selected period.
						</div>
					) : (
						<>
							<div className="overflow-x-auto">
								<table className="w-full" style={{ tableLayout: 'auto', fontSize: '14px' }}>
									<thead>
										<tr className="bg-default-100 text-default-600 text-xs font-semibold uppercase tracking-wider">
											<th data-key="eventTime" style={{ padding: '12px' }} className="text-left whitespace-nowrap rounded-l-lg">Event Time</th>
											<th data-key="lastModified" style={{ padding: '12px' }} className="text-left whitespace-nowrap">Last Modified</th>
											<th data-key="meter" style={{ padding: '12px' }} className="text-left">Meter</th>
											<th data-key="start" style={{ padding: '12px' }} className="text-right">Start</th>
											<th data-key="end" style={{ padding: '12px' }} className="text-right">End</th>
											<th data-key="total" style={{ padding: '12px' }} className="text-right">Total</th>
											<th data-key="pilot" style={{ padding: '12px' }} className="text-left whitespace-nowrap">Pilot</th>
											<th data-key="sicPilot" style={{ padding: '12px' }} className="text-left whitespace-nowrap">SIC Pilot</th>
											<th data-key="notes" style={{ padding: '12px' }} className="text-left rounded-r-lg">Notes</th>
										</tr>
									</thead>
									{history.map((entry, entryIndex) => {
										const countersData = entry.counters || [entry];
										const isEvenGroup = entryIndex % 2 === 0;
										const isClickable = entry.eventId && entry.eventStartTime;

										return (
											<tbody
												key={`${entry.eventId || 'entry'}-${entryIndex}`}
												className={`${isEvenGroup ? '' : 'bg-default-50'} ${isClickable ? 'cursor-pointer hover:bg-default-100 transition-colors' : ''} ${entryIndex > 0 ? 'border-t border-default-200' : ''}`}
												onClick={isClickable ? () => handleEventClick(entry) : undefined}
												role={isClickable ? 'button' : undefined}
												tabIndex={isClickable ? 0 : undefined}
												onKeyDown={isClickable ? e => e.key === 'Enter' && handleEventClick(entry) : undefined}
											>
												{countersData.map((counter, counterIndex) => {
													const startValue = counter.startValue ?? (
														(parseFloat(counter.value) || 0) - (parseFloat(counter.diff) || 0)
													).toFixed(2);
													const diff = parseFloat(counter.diff) || 0;
													const isFirstCounter = counterIndex === 0;

													return (
														<tr
															key={`${counter.counterId || counter.resourceId || 'c'}-${counterIndex}`}
															className={`${!isFirstCounter ? 'border-t border-dashed border-default-200' : ''}`}
														>
														<td data-key="eventTime" style={{ padding: '12px' }} className="whitespace-nowrap">
															{isFirstCounter ? (
																<span className="font-medium text-default-700">
																	{formatDateTime(entry.eventStartTime)}
																</span>
															) : null}
														</td>
														<td data-key="lastModified" style={{ padding: '12px' }} className="whitespace-nowrap text-default-500">
															{isFirstCounter ? formatDateTime(entry.dateOfChange || counter.dateOfChange) : null}
														</td>
														<td data-key="meter" style={{ padding: '12px' }}>
															<Chip
																size="sm"
																variant="flat"
																color={counter.counterType === 'hobbs' ? 'primary' : 'default'}
															>
																{counter.counterName || counter.resourceName || '-'}
															</Chip>
														</td>
														<td data-key="start" style={{ padding: '12px' }} className="text-right font-mono text-default-600 whitespace-nowrap">
															{startValue}
														</td>
														<td data-key="end" style={{ padding: '12px' }} className="text-right font-mono text-default-600 whitespace-nowrap">
															{counter.value ?? '-'}
														</td>
														<td data-key="total" style={{ padding: '12px' }} className="text-right font-mono whitespace-nowrap">
															<span className={`font-medium ${diff > 0 ? 'text-success-600' : diff < 0 ? 'text-danger-600' : 'text-default-400'}`}>
																{diff > 0 ? '+' : ''}{diff.toFixed(2)}
															</span>
														</td>
														<td data-key="pilot" style={{ padding: '12px' }} className="text-default-700 whitespace-nowrap">
															{isFirstCounter ? (entry.pilot || <span className="text-default-300">-</span>) : null}
														</td>
														<td data-key="sicPilot" style={{ padding: '12px' }} className="text-default-500 whitespace-nowrap">
															{isFirstCounter ? (entry.sicPilot || <span className="text-default-300">-</span>) : null}
														</td>
														<td data-key="notes" style={{ padding: '12px' }} className="text-default-500" title={counter.comment || ''}>
															{/* max-width on a TD is ignored by table-layout:auto — the
															    ellipsis lives on an inner block so a long note can't
															    stretch the table into a huge horizontal scroll. */}
															<div className="max-w-[250px] overflow-hidden text-ellipsis whitespace-nowrap">
																{isFirstCounter ? (counter.comment || <span className="text-default-300">-</span>) : null}
															</div>
														</td>
													</tr>
													);
												})}
											</tbody>
										);
									})}
								</table>
							</div>

							{/* Pagination */}
							{(positionHistory.length > 0 || hasNextPage) && (
								<div className="flex justify-center items-center gap-4 mt-4">
									<Button
										size="sm"
										variant="flat"
										isDisabled={loading || positionHistory.length === 0}
										onPress={() => {
											const prevPos = positionHistory[positionHistory.length - 1] ?? 0;
											setPosition(prevPos);
											setPositionHistory(prev => prev.slice(0, -1));
										}}
										startContent={<ChevronLeft className="w-4 h-4" />}
									>
										Previous
									</Button>
									<span className="text-sm text-default-500">
										Page {positionHistory.length + 1}
									</span>
									<Button
										size="sm"
										variant="flat"
										isDisabled={loading || !hasNextPage}
										onPress={() => {
											setPositionHistory(prev => [...prev, position]);
											setPosition(nextPosition);
										}}
										endContent={<ChevronRight className="w-4 h-4" />}
									>
										Next
									</Button>
								</div>
							)}
						</>
					)}
				</CardBody>
			</Card>
		</div>
	);
}

export default SimulatorMetersPage;
